import React, { useEffect, useMemo, useState } from 'react';

import { formatAddress, getUser, getWithLocale } from '../../utils/utils';
import { getSearchList, handleViewActivity } from '../../utils/requests';

import { DetailPageLayout } from '../detailPageLayout';
import dynamic from 'next/dynamic';

const NearbyLocationList = dynamic(() => import('../nearbyLocationList'));
const PropertyDetailContent = dynamic(() => import('./PropertyDetailContent'));

export const PropertyDetailSection = ({ action, data, locale, t, breadcrumb, actionsTable, actions }) => {
  const language = locale.toUpperCase();
  const [nearbyPropertyList, setNearbyPropertyList] = useState<any[]>([]);
  const [nearbyProjectList, setNearbyProjectList] = useState<any[]>([]);
  
  const getActionCategory = (data: any) => {
    const category = getWithLocale(data, 'category_Name', language);

    if (!action || !actions?.length) {
      return { actionDisplay: '', category };
    }

    let matched = actions.find(
      (item) => item[`action_${language}`]?.trim().toLowerCase() === action.trim().toLowerCase()
    );

    const actionDisplay = matched ? matched[`display_${language}`] : '';

    return { actionDisplay, category };
  };

  const actionCategory = getActionCategory(data);
  const actionCategoryDisplay =
    language === 'EN'
      ? [actionCategory.category, actionCategory.actionDisplay].join(' ')
      : actionCategory.actionDisplay + actionCategory.category;

  const actionId = actionsTable?.data?.find((item) => item.action === action)?.id;
  const propertyName = `${actionCategoryDisplay} ${
    language === 'TH' ? data?.property_Name : getWithLocale(data, 'property_name', language) || data?.property_Name
  }`;
  const address = formatAddress({
    road: data[`Road_${language}`],
    alley: data[`Alley_${language}`],
    district: data[`DISTRICT_NAME_${language}`],
    amphur: data[`AMPHUR_NAME_${language}`],
    province: data[`PROVINCE_NAME_${language}`],
    postcode: data[`POSTCODE`] || data[`POST_CODE`],
    lang: language,
  });

  useEffect(() => {
    if (!data?.category_ID) return;

    const fetchNearby = async (options: {}, setList: (items: any[]) => void) => {
      const result = await getSearchList({
        page: 1,
        limit: 12,
        lat: data.project_Latitude,
        lng: data.project_Longitude,
        ...options,
      });

      setList(result?.data?.items || []);
    };

    fetchNearby({ categoryid: data.category_ID, sell: true, rent: true, propertystatus: true }, setNearbyPropertyList);
    fetchNearby({ sell: true, rent: true, projectstatus: true, projectonly: true }, setNearbyProjectList);
  }, [data?.category_ID]);

  useEffect(() => {
    if (!data?.property_ID) return;
    const trackView = async () => {
      const user = await getUser();
      if (user) {
        handleViewActivity('property', Number(data?.property_ID), user.id);
      }
    };
    trackView();
  }, [data?.property_ID]);

  const nearbyList = useMemo(() => {
    return Object.entries(data?.Nearby_Location || {}).map(([key, value], index) => ({
      key: index.toString(),
      label: getWithLocale(value?.[0], 'location_type_name', language),
      children: <NearbyLocationList data={value} locale={locale} />,
    }));
  }, [data, locale]);

  return (
    <DetailPageLayout
      action={action}
      t={t}
      breadcrumb={breadcrumb}
      header={{
        title: propertyName,
        address: address,
        saleCount: 0,
        rentCount: 0,
        detail: data,
      }}
      detail={<PropertyDetailContent data={data} t={t} locale={locale} nearbyList={nearbyList} />}
      data={data}
      nearbyList={data?.Nearby_Location || []}
      locale={locale}
      categoryId={data?.category_ID || ''}
      actionId={actionId || ''}
      nearbyPropertyList={nearbyPropertyList}
      nearbyProjectList={nearbyProjectList}
      actions={actions}
    />
  );
};
